import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import './PublicTempSessionStyles.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicTempSessionPage() {
  const { token } = useParams();
  const location = useLocation();

  // Determine access mode: token (/s/:token) or slug (/session/xxx)
  const slug = !token ? location.pathname.replace(/^\/session\//, '') : null;
  const accessKey = token || slug;
  const apiBase = token
    ? `${API_URL}/api/temp-sessions/public/${token}`
    : `${API_URL}/api/temp-sessions/public/slug/${slug}`;

  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState(null);
  const [error, setError] = useState(null);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Content viewer
  const [viewingContent, setViewingContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const contentRef = useRef(null);

  // Attendance
  const [attendanceForm, setAttendanceForm] = useState({
    candidate_name: '', candidate_email: '', candidate_phone: ''
  });
  const [attendanceConfirmed, setAttendanceConfirmed] = useState(null);
  const [attendanceSubmitting, setAttendanceSubmitting] = useState(false);
  const [attendanceError, setAttendanceError] = useState('');
  const [confirmedDates, setConfirmedDates] = useState([]);
  const [showAttendanceForm, setShowAttendanceForm] = useState(false);
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);

  // Exam registration for public sessions
  const [examRegistration, setExamRegistration] = useState(null); // pending item
  const [examCandidate, setExamCandidate] = useState({ nom: '', prenom: '', email: '' });
  const [examRegError, setExamRegError] = useState('');

  // Resource viewer (files or table)
  const [viewingResource, setViewingResource] = useState(null);

  // Email verification for secure files
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [fileEmail, setFileEmail] = useState('');
  const [fileEmailError, setFileEmailError] = useState('');
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [verifiedFiles, setVerifiedFiles] = useState(null);

  // Contact form
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  // Branding from session settings
  const sess = sessionData?.session || {};
  const brandName = sess.branding_name || 'Takapes Learn';
  const brandUrl = sess.branding_url || 'https://takapes.info';
  const brandFooter = sess.branding_footer || 'Propulsé par';
  const brandExtra = sess.branding_footer_extra || 'Session sécurisée';
  const customCss = sess.custom_css || '';

  // Inject custom CSS
  useEffect(() => {
    if (!customCss) return;
    const style = document.createElement('style');
    style.setAttribute('data-session-css', 'true');
    style.textContent = customCss;
    document.head.appendChild(style);
    return () => style.remove();
  }, [customCss]);

  useEffect(() => { loadSession(); }, [accessKey]);

  // Listen for fullscreen changes (e.g. user presses Escape)
  useEffect(() => {
    const handler = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const loadSession = async (pwd = null) => {
    try {
      const params = new URLSearchParams();
      if (pwd) params.set('password', pwd);
      // Forward preview param from URL
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('preview') === 'true') params.set('preview', 'true');
      const res = await fetch(`${apiBase}?${params}`);
      const data = await res.json();

      if (data.error === 'password_required') {
        setPasswordRequired(true);
        setError(null);
        setSessionData({ title: data.title });
      } else if (data.error === 'not_started') {
        setError({ type: 'pending', message: data.message, title: data.title, start_at: data.start_at });
      } else if (data.error === 'expired') {
        setError({ type: 'expired', message: data.message, title: data.title });
      } else if (data.error === 'not_published') {
        setError({ type: 'pending', message: data.message });
      } else if (data.session) {
        setSessionData(data);
        setPasswordRequired(false);
        setError(null);
      } else if (res.status === 403 || res.status === 404) {
        setError({ type: 'expired', message: data.detail || data.message || 'Lien invalide' });
      }
    } catch (err) {
      setError({ type: 'expired', message: 'Impossible de charger la session' });
    } finally {
      setLoading(false);
      setVerifying(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setVerifying(true);
    await loadSession(password);
  };

  const handleViewContent = async (item) => {
    // For exams, show registration dialog first
    if (item.item_type === 'exam') {
      setExamRegistration(item);
      setExamCandidate({ nom: '', prenom: '', email: '' });
      setExamRegError('');
      return;
    }
    await loadContent(item);
  };

  const loadContent = async (item) => {
    setLoadingContent(true);
    try {
      const params = new URLSearchParams();
      if (password) params.set('password', password);
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('preview') === 'true') params.set('preview', 'true');
      const res = await fetch(`${apiBase}/content/${item.item_id}?${params}`);
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail || 'Erreur d\'accès');
        return;
      }
      const data = await res.json();
      setViewingContent({ ...data, item });
    } catch (err) {
      alert('Erreur de chargement du contenu');
    } finally {
      setLoadingContent(false);
    }
  };

  const handleExamRegSubmit = async (e) => {
    e.preventDefault();
    if (!examCandidate.nom.trim() || !examCandidate.prenom.trim()) {
      setExamRegError('Le nom et le pr\u00e9nom sont requis');
      return;
    }
    if (!examCandidate.email.trim() || !examCandidate.email.includes('@')) {
      setExamRegError('Un email valide est requis');
      return;
    }
    // Registration OK — load the exam content with candidate info
    const item = examRegistration;
    setExamRegistration(null);
    setLoadingContent(true);
    try {
      const params = new URLSearchParams();
      if (password) params.set('password', password);
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('preview') === 'true') params.set('preview', 'true');
      const res = await fetch(`${apiBase}/content/${item.item_id}?${params}`);
      if (!res.ok) {
        const err = await res.json();
        alert(err.detail || 'Erreur d\'accès');
        return;
      }
      const data = await res.json();
      setViewingContent({ ...data, item, candidate: { ...examCandidate } });
    } catch (err) {
      alert('Erreur de chargement du contenu');
    } finally {
      setLoadingContent(false);
    }
  };

  const toggleFullscreen = useCallback(async () => {
    try {
      if (!isFullscreen) {
        const el = contentRef.current;
        if (el) {
          if (el.requestFullscreen) await el.requestFullscreen();
          else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
          setIsFullscreen(true);
        }
      } else {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  }, [isFullscreen]);

  // ===== Signature Pad Functions =====
  const getCanvasCoords = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoords(e);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1e293b';
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSigned(true);
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
  };

  const getSignatureData = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;
    return canvas.toDataURL('image/png');
  };

  const handleConfirmAttendance = async () => {
    if (!attendanceForm.candidate_name.trim()) {
      setAttendanceError('Le nom est requis');
      return;
    }
    const session = sessionData?.session;
    if (session?.attendance_require_email && !attendanceForm.candidate_email) {
      setAttendanceError("L'email est requis");
      return;
    }
    if (session?.attendance_require_phone && !attendanceForm.candidate_phone) {
      setAttendanceError('Le téléphone est requis');
      return;
    }
    if (!hasSigned) {
      setAttendanceError('La signature est obligatoire');
      return;
    }

    setAttendanceSubmitting(true);
    setAttendanceError('');
    try {
      const signatureData = getSignatureData();
      const res = await fetch(`${apiBase}/attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...attendanceForm, signature_data: signatureData })
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur');
      }
      const data = await res.json();
      setAttendanceConfirmed(data);
      setConfirmedDates(prev => [...prev, data.attendance_date]);
      setShowAttendanceForm(false);
      clearSignature();
    } catch (err) {
      setAttendanceError(err.message);
    } finally {
      setAttendanceSubmitting(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactForm.message.trim()) return;
    setContactSubmitting(true);
    try {
      const res = await fetch(`${apiBase}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur');
      }
      setContactSuccess(true);
      setContactForm({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setContactSuccess(false), 5000);
    } catch (err) {
      alert(err.message || 'Erreur d\'envoi');
    } finally {
      setContactSubmitting(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('fr-FR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch { return d; }
  };

  const formatDateTime = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return d; }
  };

  const formatDayName = (d) => {
    if (!d) return '—';
    try {
      return new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch { return d; }
  };

  const itemIcons = {
    course: '📚', lab: '🧪', quiz: '✅', exam: '📝', url: '🔗'
  };

  const itemLabels = {
    course: 'Cours', lab: 'TP', quiz: 'Quiz', exam: 'Examen', url: 'URL'
  };

  // ============ LOADING ============
  if (loading) {
    return (
      <main className="ts-page">
        <div className="ts-shell">
          <div className="ts-loading">
            <Loader2 className="ts-loading-spinner" />
            <p style={{ color: '#94a3b8' }}>Chargement de la session...</p>
          </div>
        </div>
      </main>
    );
  }

  // ============ ERROR STATES ============
  if (error) {
    return (
      <main className="ts-page">
        <div className="ts-shell">
          <div className="ts-hero">
            <span className="ts-badge">
              📅 Session temporaire
            </span>
            {error.title && <h1>{error.title}</h1>}
          </div>
          <div className="ts-status-card">
            {error.type === 'expired' ? (
              <>
                <svg className="ts-status-icon expired" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 7v5l3 2"/>
                </svg>
                <h2>Session terminée</h2>
                <p>{error.message}</p>
              </>
            ) : (
              <>
                <svg className="ts-status-icon pending" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 7v5l3 2"/>
                </svg>
                <h2>Session non encore disponible</h2>
                <p>{error.message}</p>
                {error.start_at && (
                  <p style={{ marginTop: '0.75rem', color: '#a78bfa', fontSize: '0.9rem' }}>
                    Disponible à partir du {formatDateTime(error.start_at)}
                  </p>
                )}
              </>
            )}
          </div>
          <div className="ts-footer">
            {brandFooter} <a href={brandUrl}>{brandName}</a>
          </div>
        </div>
      </main>
    );
  }

  // ============ PASSWORD REQUIRED ============
  if (passwordRequired) {
    return (
      <main className="ts-page">
        <div className="ts-shell">
          <div className="ts-hero">
            <span className="ts-badge">🔒 Session protégée</span>
            {sessionData?.title && <h1>{sessionData.title}</h1>}
          </div>
          <div className="ts-status-card">
            <svg className="ts-status-icon locked" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <h2>Accès protégé</h2>
            <p>Cette session nécessite un mot de passe pour y accéder.</p>
            <form className="ts-password-form" onSubmit={handlePasswordSubmit}>
              <input
                className="ts-input"
                type="password"
                placeholder="Mot de passe..."
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoFocus
              />
              <button className="ts-button ts-button-primary" type="submit" disabled={verifying}>
                {verifying ? '⏳ Vérification...' : '🔓 Accéder'}
              </button>
            </form>
          </div>
          <div className="ts-footer">
            {brandFooter} <a href={brandUrl}>{brandName}</a>
          </div>
        </div>
      </main>
    );
  }

  // ============ CONTENT VIEWER ============
  if (viewingContent) {
    const content = viewingContent.content;
    const itemType = viewingContent.item_type;
    return (
      <main className="ts-page">
        <div className="ts-shell">
          <div className="ts-content-topbar">
            <button className="ts-back-btn" onClick={() => { setViewingContent(null); if(document.fullscreenElement) document.exitFullscreen(); setIsFullscreen(false); }}>
              ← Retour à la session
            </button>
          </div>
          <div className="ts-glass">
            <h2>
              <span>{itemIcons[itemType] || '📄'}</span>
              {content?.title || 'Contenu'}
            </h2>
          </div>
          <div className={`ts-content-viewer ${isFullscreen ? 'ts-content-fullscreen' : ''}`} ref={contentRef}>
            <button
              className="ts-fullscreen-btn ts-fullscreen-float"
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
            >
              {isFullscreen ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                </svg>
              )}
              <span>{isFullscreen ? 'Réduire' : 'Plein écran'}</span>
            </button>
            {itemType === 'course' && (
              <div>
                {content?.description && <p style={{ marginBottom: '1.5rem', fontStyle: 'italic', color: '#94a3b8' }}>{content.description}</p>}
                {content?.content && <div dangerouslySetInnerHTML={{ __html: content.content }} />}
                {content?.sections?.map((section, i) => (
                  <div key={section.id || i} style={{ marginBottom: '2rem' }}>
                    <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.75rem', color: '#c4b5fd' }}>
                      {section.title}
                    </h3>
                    {section.content && <div dangerouslySetInnerHTML={{ __html: section.content }} />}
                  </div>
                ))}
              </div>
            )}
            {itemType === 'lab' && (
              <div>
                {content?.description && <p style={{ marginBottom: '1rem', fontStyle: 'italic', color: '#94a3b8' }}>{content.description}</p>}
                {content?.content && <div dangerouslySetInnerHTML={{ __html: content.content }} />}
              </div>
            )}
            {(itemType === 'quiz' || itemType === 'exam') && (
              <div>
                {viewingContent?.candidate && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem',
                    padding: '0.75rem 1rem', borderRadius: '0.75rem',
                    background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)'
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>👤</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#86efac' }}>
                        {viewingContent.candidate.prenom} {viewingContent.candidate.nom}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                        {viewingContent.candidate.email}
                      </div>
                    </div>
                  </div>
                )}
                {content?.description && <p style={{ marginBottom: '1rem', fontStyle: 'italic', color: '#94a3b8' }}>{content.description}</p>}
                {content?.settings && (
                  <div style={{
                    display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem',
                    padding: '0.75rem 1rem', borderRadius: '0.75rem',
                    background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.15)'
                  }}>
                    {content.settings.duration_minutes && (
                      <span style={{ fontSize: '0.85rem', color: '#c4b5fd' }}>
                        ⏱ {content.settings.duration_minutes} min
                      </span>
                    )}
                    <span style={{ fontSize: '0.85rem', color: '#c4b5fd' }}>
                      📝 {content.questions?.length || 0} questions
                    </span>
                    {content.settings.passing_score && (
                      <span style={{ fontSize: '0.85rem', color: '#c4b5fd' }}>
                        🎯 Score min: {content.settings.passing_score}%
                      </span>
                    )}
                  </div>
                )}
                {content?.questions?.map((q, i) => {
                  const questionChoices = q.choices || q.options || [];
                  return (
                    <div key={q.id || i} style={{
                      marginBottom: '1.5rem', padding: '1.25rem', borderRadius: '0.75rem',
                      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                        <p style={{ fontWeight: 600, flex: 1 }}>
                          <span style={{ color: '#a78bfa' }}>Q{i + 1}.</span> {q.question_text}
                        </p>
                        <span style={{
                          fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'nowrap', marginLeft: '0.5rem',
                          padding: '0.2rem 0.5rem', borderRadius: '0.4rem', background: 'rgba(255,255,255,0.05)'
                        }}>
                          {q.points || 1} pt{(q.points || 1) > 1 ? 's' : ''}
                        </span>
                      </div>
                      {questionChoices.map((opt, j) => {
                        const optText = typeof opt === 'object' ? opt.text : opt;
                        return (
                          <div key={opt.id || j} style={{
                            padding: '0.6rem 0.85rem', marginTop: '0.4rem',
                            borderRadius: '0.5rem', background: 'rgba(255,255,255,0.03)',
                            border: '1px solid rgba(255,255,255,0.06)', fontSize: '0.88rem',
                            cursor: 'default', display: 'flex', alignItems: 'center', gap: '0.5rem'
                          }}>
                            <span style={{
                              width: '18px', height: '18px', borderRadius: q.type === 'multiple_choice' ? '3px' : '50%',
                              border: '2px solid rgba(124,58,237,0.3)', flexShrink: 0
                            }} />
                            {optText}
                          </div>
                        );
                      })}
                      {q.type === 'short_answer' && (
                        <div style={{
                          marginTop: '0.5rem', padding: '0.6rem 0.85rem', borderRadius: '0.5rem',
                          background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.1)',
                          color: '#64748b', fontSize: '0.85rem'
                        }}>
                          Réponse courte...
                        </div>
                      )}
                      {q.type === 'long_answer' && (
                        <div style={{
                          marginTop: '0.5rem', padding: '0.6rem 0.85rem', borderRadius: '0.5rem',
                          background: 'rgba(255,255,255,0.03)', border: '1px dashed rgba(255,255,255,0.1)',
                          color: '#64748b', fontSize: '0.85rem', minHeight: '80px'
                        }}>
                          Réponse longue...
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="ts-footer">
            {brandFooter} <a href={brandUrl}>{brandName}</a>
          </div>
        </div>
      </main>
    );
  }

  // Shared data & helpers (needed by both resource viewer and main view)
  const hasFiles = sessionData?.has_files || false;
  const hasTables = sessionData?.has_tables || false;
  const hasLinks = sessionData?.has_links || false;
  const hasResources = hasFiles || hasTables || hasLinks;

  const formatFileSizePublic = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getFileIconPublic = (type) => {
    if (type?.startsWith('image/')) return '🖼️';
    if (type?.startsWith('video/')) return '🎬';
    if (type?.startsWith('audio/')) return '🎵';
    if (type?.includes('pdf')) return '📄';
    if (type?.includes('zip') || type?.includes('rar')) return '📦';
    if (type?.includes('spreadsheet') || type?.includes('excel') || type?.includes('csv')) return '📊';
    if (type?.includes('document') || type?.includes('word')) return '📝';
    if (type?.includes('presentation') || type?.includes('powerpoint')) return '📽️';
    return '📎';
  };

  // Fetch resources (files + tables) filtered by email
  const handleFileEmailSubmit = async (e) => {
    e?.preventDefault();
    if (!fileEmail.trim() || !fileEmail.includes('@')) {
      setFileEmailError('Veuillez saisir un email valide');
      return;
    }
    setFileEmailError('');
    setLoadingFiles(true);
    try {
      const params = new URLSearchParams({ email: fileEmail.trim() });
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('preview') === 'true') params.set('preview', 'true');
      const res = await fetch(`${apiBase}/resources?${params}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Erreur');
      }
      const data = await res.json();
      const files = data.files || [];
      const tables = (data.tables || []).filter(t => t.columns?.length > 0);
      const links = data.links || [];
      setVerifiedFiles(files);
      setShowEmailModal(false);
      setViewingResource({
        type: 'resources',
        title: 'Mes Ressources',
        files,
        tables,
        links,
        email: data.email
      });
    } catch (err) {
      setFileEmailError(err.message || 'Erreur de vérification');
    } finally {
      setLoadingFiles(false);
    }
  };

  // ============ RESOURCE VIEWER (Files / Table) ============
  if (viewingResource) {
    return (
      <main className="ts-page">
        <div className="ts-shell">
          <div className="ts-content-topbar">
            <button className="ts-back-btn" onClick={() => setViewingResource(null)}>
              ← Retour à la session
            </button>
          </div>
          <div className="ts-glass">
            <h2>
              <span>📂</span>
              {viewingResource.title}
            </h2>
            {viewingResource.email && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.5rem 0.75rem', marginTop: '0.75rem',
                borderRadius: '0.5rem',
                background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.15)',
                fontSize: '0.85rem', color: '#c4b5fd'
              }}>
                <span>📧</span>
                Ressources pour <strong style={{ color: '#e2e8f0' }}>{viewingResource.email}</strong>
                <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: '#64748b' }}>
                  {(viewingResource.files?.length || 0)} fichier{(viewingResource.files?.length || 0) > 1 ? 's' : ''}
                  {viewingResource.links?.length > 0 && ` • ${viewingResource.links.length} lien${viewingResource.links.length > 1 ? 's' : ''}`}
                  {viewingResource.tables?.length > 0 && ` • ${viewingResource.tables.length} tableau${viewingResource.tables.length > 1 ? 'x' : ''}`}
                </span>
              </div>
            )}
          </div>
          <div className="ts-content-viewer">
            {/* Files section */}
            {viewingResource.type === 'resources' && viewingResource.files?.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#c4b5fd', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  📎 Fichiers
                </h3>
                {viewingResource.files.length === 0 ? (
                  <div style={{
                    textAlign: 'center', padding: '3rem 1.5rem',
                    color: '#64748b'
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                    <p style={{ fontWeight: 600, fontSize: '1rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                      Aucun fichier trouvé
                    </p>
                    <p style={{ fontSize: '0.85rem' }}>
                      Aucun fichier n'est associé à cet email.
                    </p>
                  </div>
                ) : (
                  viewingResource.files.map((f) => (
                    <div
                      key={f.id}
                      onClick={() => window.open(`${API_URL}${f.url}?dl=1`, '_blank')}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.85rem',
                        padding: '0.85rem 1rem', marginBottom: '0.5rem',
                        borderRadius: '0.75rem', cursor: 'pointer',
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(255,255,255,0.06)',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.08)'; e.currentTarget.style.borderColor = 'rgba(124,58,237,0.2)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}
                    >
                      <span style={{ fontSize: '1.4rem' }}>{getFileIconPublic(f.file_type)}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#e2e8f0' }}>{f.original_name}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{formatFileSizePublic(f.file_size)}</div>
                      </div>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px', color: '#a78bfa', flexShrink: 0 }}>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="7 10 12 15 17 10"/>
                        <line x1="12" y1="15" x2="12" y2="3"/>
                      </svg>
                    </div>
                  ))
                )}
              </div>
            )}
            {/* Shared Links section */}
            {viewingResource.type === 'resources' && viewingResource.links?.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#c4b5fd', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  🔗 Liens partagés
                </h3>
                {viewingResource.links.map((link) => (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.85rem',
                      padding: '0.85rem 1rem', marginBottom: '0.5rem',
                      borderRadius: '0.75rem', cursor: 'pointer',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      transition: 'all 0.15s',
                      textDecoration: 'none',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.08)'; e.currentTarget.style.borderColor = 'rgba(124,58,237,0.2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}
                  >
                    <span style={{ fontSize: '1.4rem' }}>🔗</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#e2e8f0' }}>{link.title || link.url}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link.url}</div>
                    </div>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px', color: '#a78bfa', flexShrink: 0 }}>
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </a>
                ))}
              </div>
            )}
            {/* Tables section */}
            {viewingResource.type === 'resources' && viewingResource.tables?.length > 0 && (
              <div>
                <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: '#c4b5fd', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  📊 Tableaux
                </h3>
                {viewingResource.tables.map((tbl) => (
                  <div key={tbl.id} style={{ marginBottom: '1.5rem' }}>
                    <div style={{
                      fontSize: '0.9rem', fontWeight: 600, color: '#e2e8f0',
                      marginBottom: '0.5rem', padding: '0.5rem 0.75rem',
                      background: 'rgba(124,58,237,0.05)', borderRadius: '0.5rem',
                      display: 'flex', alignItems: 'center', gap: '0.5rem'
                    }}>
                      📊 {tbl.name || 'Tableau'}
                      <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: 'auto' }}>
                        {(tbl.rows || []).length} ligne{(tbl.rows || []).length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div style={{ overflowX: 'auto', borderRadius: '0.75rem', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ background: 'rgba(124,58,237,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                            <th style={{ padding: '0.7rem 0.85rem', textAlign: 'left', color: '#a78bfa', fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>#</th>
                            {(tbl.columns || []).map((col) => (
                              <th key={col.id} style={{ padding: '0.7rem 0.85rem', textAlign: 'left', color: '#a78bfa', fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                {col.name}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(tbl.rows || []).length === 0 ? (
                            <tr>
                              <td colSpan={(tbl.columns || []).length + 1} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                                Aucune donnée
                              </td>
                            </tr>
                          ) : (
                            tbl.rows.map((row, idx) => (
                              <tr key={row._id || idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                <td style={{ padding: '0.6rem 0.85rem', color: '#64748b', fontFamily: 'monospace', fontSize: '0.8rem' }}>{idx + 1}</td>
                                {(tbl.columns || []).map((col) => (
                                  <td key={col.id} style={{ padding: '0.6rem 0.85rem', color: '#e2e8f0' }}>
                                    {row[col.id] || '—'}
                                  </td>
                                ))}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Empty state for resources */}
            {viewingResource.type === 'resources' && (viewingResource.files?.length || 0) === 0 && (viewingResource.links?.length || 0) === 0 && (viewingResource.tables?.length || 0) === 0 && (
              <div style={{
                textAlign: 'center', padding: '3rem 1.5rem',
                color: '#64748b'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                <p style={{ fontWeight: 600, fontSize: '1rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                  Aucune ressource trouvée
                </p>
                <p style={{ fontSize: '0.85rem' }}>
                  Aucun fichier ou tableau n'est associé à cet email.
                </p>
              </div>
            )}
            {viewingResource.type === 'table' && (
              <div style={{ overflowX: 'auto', borderRadius: '0.75rem', border: '1px solid rgba(255,255,255,0.06)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ background: 'rgba(124,58,237,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                      <th style={{ padding: '0.7rem 0.85rem', textAlign: 'left', color: '#a78bfa', fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>#</th>
                      {viewingResource.data.columns.map((col) => (
                        <th key={col.id} style={{ padding: '0.7rem 0.85rem', textAlign: 'left', color: '#a78bfa', fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {col.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(viewingResource.data.rows || []).length === 0 ? (
                      <tr>
                        <td colSpan={viewingResource.data.columns.length + 1} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                          Aucune donnée
                        </td>
                      </tr>
                    ) : (
                      viewingResource.data.rows.map((row, idx) => (
                        <tr key={row._id || idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td style={{ padding: '0.6rem 0.85rem', color: '#64748b', fontFamily: 'monospace', fontSize: '0.8rem' }}>{idx + 1}</td>
                          {viewingResource.data.columns.map((col) => (
                            <td key={col.id} style={{ padding: '0.6rem 0.85rem', color: '#e2e8f0' }}>
                              {row[col.id] || '—'}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="ts-footer">
            {brandFooter} <a href={brandUrl}>{brandName}</a>
          </div>
        </div>
      </main>
    );
  }

  // ============ MAIN SESSION VIEW ============
  const session = sessionData?.session;
  const items = sessionData?.items || [];
  const attendanceDays = sessionData?.attendance_days || [];

  return (
    <main className="ts-page">
      <div className="ts-shell">
        {/* Hero */}
        <div className="ts-hero">
          <span className="ts-badge">
            📅 Session temporaire
          </span>
          <h1>{session?.title}</h1>
          {session?.description && <p>{session.description}</p>}
        </div>

        {/* Info bar */}
        <div className="ts-info-bar">
          <span className="ts-info-item">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/>
            </svg>
            {formatDateTime(session?.start_at)}
          </span>
          <span className="ts-info-item">→</span>
          <span className="ts-info-item">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
            </svg>
            {formatDateTime(session?.end_at)}
          </span>
          <span className="ts-info-item">
            🌍 {session?.timezone}
          </span>
        </div>

        {/* ====== 3-COLUMN LAYOUT ====== */}
        <div className={`ts-layout ${(sessionData?.program || []).length === 0 ? 'no-program' : ''}`}>
          {/* LEFT SIDEBAR - Programme */}
          {(sessionData?.program || []).length > 0 && (
            <div className="ts-sidebar ts-sidebar-left">
              <div className="ts-glass">
                <h2>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M8 6h13"/><path d="M8 12h13"/><path d="M8 18h13"/>
                    <path d="M3 6h.01"/><path d="M3 12h.01"/><path d="M3 18h.01"/>
                  </svg>
                  Programme
                </h2>
                <div className="ts-program">
                  {(sessionData?.program || []).map((item, i) => (
                    <div key={item.id || i} className="ts-program-item">
                      {item.time && (
                        <div className="ts-program-time">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '0.85rem', height: '0.85rem' }}>
                            <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                          </svg>
                          {item.time}
                        </div>
                      )}
                      <div className="ts-program-title">{item.title}</div>
                      {item.description && (
                        <div className="ts-program-desc">{item.description}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* CENTER - Main Content */}
          <div className="ts-main-content">
            {/* Content Items */}
            {items.length > 0 && (
              <div className="ts-glass">
                <h2>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                  </svg>
                  Contenus disponibles
                </h2>
                <div className="ts-items">
                  {items.filter(item => item.visible !== false).map((item) => (
                    <div
                      key={item.id}
                      className="ts-item"
                      onClick={() => {
                        if (item.item_type === 'url') {
                          window.open(item.item_id, '_blank', 'noopener,noreferrer');
                        } else {
                          handleViewContent(item);
                        }
                      }}
                    >
                      <div className={`ts-item-icon ${item.item_type}`}>
                        {itemIcons[item.item_type] || '📄'}
                      </div>
                      <span className="ts-item-title">{item.item_title || 'Sans titre'}</span>
                      <span className={`ts-item-type ${item.item_type}`}>
                        {itemLabels[item.item_type] || 'Contenu'}
                      </span>
                      <svg className="ts-item-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 18l6-6-6-6"/>
                      </svg>
                    </div>
                  ))}
                </div>
                {loadingContent && (
                  <div style={{ textAlign: 'center', padding: '1rem' }}>
                    <Loader2 className="ts-loading-spinner" style={{ width: '1.5rem', height: '1.5rem' }} />
                  </div>
                )}
              </div>
            )}

            {/* Resources - email-gated access */}
            {hasResources && (
              <div className="ts-glass">
                <h2>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                  </svg>
                  Ressources
                </h2>
                <div className="ts-items">
                  <div
                    className="ts-item"
                    onClick={() => {
                      setFileEmail('');
                      setFileEmailError('');
                      setShowEmailModal(true);
                    }}
                  >
                    <div className="ts-item-icon" style={{ fontSize: '1.3rem' }}>🔐</div>
                    <span className="ts-item-title">Mes Ressources</span>
                    <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: 'auto', marginRight: '0.5rem' }}>
                      Vérification email
                    </span>
                    <svg className="ts-item-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18l6-6-6-6"/>
                    </svg>
                  </div>
                </div>
              </div>
            )}

            {/* Shortlist - Liste des Retenus (only if shortlist has candidates) */}
            {(sessionData?.shortlist || []).length > 0 && (
              <div className="ts-glass">
                <h2>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <polyline points="16 11 18 13 22 9"/>
                  </svg>
                  Liste des Retenus
                  <span style={{
                    marginLeft: '0.5rem', fontSize: '0.75rem', fontWeight: 600,
                    padding: '0.15rem 0.5rem', borderRadius: '9999px',
                    background: 'rgba(34,197,94,0.15)', color: '#86efac', border: '1px solid rgba(34,197,94,0.25)'
                  }}>
                    {sessionData.shortlist.length} candidat{sessionData.shortlist.length > 1 ? 's' : ''}
                  </span>
                </h2>
                <div style={{ overflowX: 'auto', borderRadius: '0.75rem', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: 'rgba(124,58,237,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                        <th style={{ padding: '0.7rem 0.85rem', textAlign: 'left', color: '#a78bfa', fontWeight: 600, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>#</th>
                        <th style={{ padding: '0.7rem 0.85rem', textAlign: 'left', color: '#a78bfa', fontWeight: 600, fontSize: '0.8rem' }}>Nom</th>
                        <th style={{ padding: '0.7rem 0.85rem', textAlign: 'left', color: '#a78bfa', fontWeight: 600, fontSize: '0.8rem' }}>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessionData.shortlist.map((candidate, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td style={{ padding: '0.6rem 0.85rem', color: '#64748b', fontFamily: 'monospace', fontSize: '0.8rem' }}>{idx + 1}</td>
                          <td style={{ padding: '0.6rem 0.85rem', color: '#e2e8f0', fontWeight: 500 }}>{candidate.name}</td>
                          <td style={{ padding: '0.6rem 0.85rem' }}>
                            <span style={{
                              display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '9999px',
                              fontSize: '0.75rem', fontWeight: 600,
                              background: 'rgba(34,197,94,0.12)', color: '#86efac', border: '1px solid rgba(34,197,94,0.2)'
                            }}>
                              ✅ Retenu
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Attendance Section */}
            {session?.attendance_enabled && attendanceDays.length > 0 && (
              <div className="ts-glass">
                <h2>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  Ma Présence
                </h2>
                {session.attendance_message && (
                  <div style={{
                    padding: '0.75rem 1rem', marginBottom: '0.75rem',
                    borderRadius: '0.75rem',
                    background: 'rgba(124,58,237,0.08)',
                    border: '1px solid rgba(124,58,237,0.15)',
                    fontSize: '0.88rem', color: '#c4b5fd', lineHeight: '1.5'
                  }}>
                    💬 {session.attendance_message}
                  </div>
                )}
                <div className="ts-attendance-list">
                  {attendanceDays.map(day => {
                    const isConfirmed = confirmedDates.includes(day.date);
                    const dayMsg = session.attendance_day_messages?.[day.date];
                    return (
                      <div key={day.date} className={`ts-attendance-day ${day.status} ${isConfirmed ? 'confirmed' : ''}`}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', flex: 1, minWidth: 0 }}>
                          <span className="ts-attendance-date">{formatDate(day.date)}</span>
                          {dayMsg && (
                            <span style={{ fontSize: '0.8rem', color: '#a78bfa', fontStyle: 'italic' }}>
                              📌 {dayMsg}
                            </span>
                          )}
                        </div>
                        <div className="ts-attendance-actions">
                          {isConfirmed ? (
                            <span className="ts-attendance-status confirmed">✅ Confirmé</span>
                          ) : attendanceConfirmed?.attendance_date === day.date ? (
                            <span className="ts-attendance-status confirmed">✅ Confirmé</span>
                          ) : day.status === 'today' ? (
                            <>
                              <span className="ts-attendance-status today">Aujourd'hui</span>
                              {!showAttendanceForm && (
                                <button
                                  className="ts-btn-primary"
                                  onClick={() => setShowAttendanceForm(true)}
                                  style={{ padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
                                >
                                  Confirmer
                                </button>
                              )}
                            </>
                          ) : day.status === 'past' ? (
                            day.present_count > 0 ? (
                              <span className="ts-attendance-status confirmed">✅ {day.present_count} présent{day.present_count > 1 ? 's' : ''}</span>
                            ) : (
                              <span className="ts-attendance-status absent">Absent</span>
                            )
                          ) : (
                            <span className="ts-attendance-status future">À venir</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {showAttendanceForm && (
                  <div className="ts-attendance-form">
                    <h3 style={{ fontSize: '0.95rem', marginBottom: '0.75rem', fontWeight: 600 }}>
                      📝 Confirmer votre présence
                    </h3>
                    {attendanceError && (
                      <div style={{
                        padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                        background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
                        color: '#f87171', fontSize: '0.85rem', marginBottom: '0.75rem'
                      }}>
                        {attendanceError}
                      </div>
                    )}
                    <input
                      type="text"
                      placeholder="Votre nom complet *"
                      value={attendanceForm.candidate_name}
                      onChange={(e) => setAttendanceForm(p => ({ ...p, candidate_name: e.target.value }))}
                    />
                    {session?.attendance_require_email && (
                      <input
                        type="email"
                        placeholder="Votre email *"
                        value={attendanceForm.candidate_email}
                        onChange={(e) => setAttendanceForm(p => ({ ...p, candidate_email: e.target.value }))}
                      />
                    )}
                    {session?.attendance_require_phone && (
                      <input
                        type="tel"
                        placeholder="Votre téléphone *"
                        value={attendanceForm.candidate_phone}
                        onChange={(e) => setAttendanceForm(p => ({ ...p, candidate_phone: e.target.value }))}
                      />
                    )}

                    {/* Signature Pad */}
                    <div style={{ marginTop: '0.75rem' }}>
                      <label style={{ fontSize: '0.85rem', color: '#c4b5fd', display: 'block', marginBottom: '0.4rem' }}>
                        ✍️ Votre signature *
                      </label>
                      <div style={{
                        position: 'relative', borderRadius: '0.75rem', overflow: 'hidden',
                        border: '2px solid rgba(124,58,237,0.3)', background: 'rgba(255,255,255,0.95)'
                      }}>
                        <canvas
                          ref={signatureCanvasRef}
                          width={360}
                          height={120}
                          style={{ width: '100%', height: '120px', cursor: 'crosshair', touchAction: 'none' }}
                          onMouseDown={startDrawing}
                          onMouseMove={draw}
                          onMouseUp={stopDrawing}
                          onMouseLeave={stopDrawing}
                          onTouchStart={startDrawing}
                          onTouchMove={draw}
                          onTouchEnd={stopDrawing}
                        />
                        <button
                          type="button"
                          onClick={clearSignature}
                          style={{
                            position: 'absolute', top: '4px', right: '4px',
                            background: 'rgba(239,68,68,0.8)', color: '#fff',
                            border: 'none', borderRadius: '0.4rem',
                            padding: '0.2rem 0.5rem', fontSize: '0.7rem', cursor: 'pointer'
                          }}
                        >
                          Effacer
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <button
                        className="ts-button"
                        onClick={handleConfirmAttendance}
                        disabled={attendanceSubmitting}
                        style={{ flex: 1 }}
                      >
                        {attendanceSubmitting ? '⏳ Envoi...' : '✅ Confirmer ma présence'}
                      </button>
                      <button
                        className="ts-button"
                        onClick={() => { setShowAttendanceForm(false); clearSignature(); }}
                        style={{ background: 'rgba(255,255,255,0.06)', color: '#94a3b8', flex: 'none' }}
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT SIDEBAR - Contact Form */}
          <div className="ts-sidebar ts-sidebar-right">
            <div className="ts-glass">
              <h2>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                Nous contacter
              </h2>
              {contactSuccess ? (
                <div className="ts-contact-success">
                  <div className="ts-contact-success-icon">✅</div>
                  <p>Votre message a été envoyé avec succès !</p>
                  <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.25rem' }}>Nous vous répondrons dans les meilleurs délais.</p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="ts-contact-form">
                  <div className="ts-contact-field">
                    <label>Nom *</label>
                    <input
                      type="text"
                      placeholder="Votre nom"
                      value={contactForm.name}
                      onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="ts-contact-field">
                    <label>Email</label>
                    <input
                      type="email"
                      placeholder="votre@email.com"
                      value={contactForm.email}
                      onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))}
                    />
                  </div>
                  <div className="ts-contact-field">
                    <label>Sujet</label>
                    <input
                      type="text"
                      placeholder="Objet de votre demande"
                      value={contactForm.subject}
                      onChange={e => setContactForm(p => ({ ...p, subject: e.target.value }))}
                    />
                  </div>
                  <div className="ts-contact-field">
                    <label>Message *</label>
                    <textarea
                      placeholder="Votre message..."
                      value={contactForm.message}
                      onChange={e => setContactForm(p => ({ ...p, message: e.target.value }))}
                      rows={4}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="ts-btn-primary"
                    disabled={contactSubmitting}
                    style={{ width: '100%', marginTop: '0.5rem' }}
                  >
                    {contactSubmitting ? '⏳ Envoi...' : '📨 Envoyer'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Email Verification Modal for Files */}
        {showEmailModal && (
          <div className="ts-modal-overlay" onClick={() => setShowEmailModal(false)}>
            <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ts-modal-header">
                <h3>🔐 Accès aux ressources</h3>
                <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                  Saisissez votre email pour accéder à vos fichiers et tableaux
                </p>
              </div>
              <form onSubmit={handleFileEmailSubmit} className="ts-modal-body">
                {fileEmailError && (
                  <div style={{
                    padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
                    color: '#f87171', fontSize: '0.85rem', marginBottom: '0.75rem'
                  }}>
                    {fileEmailError}
                  </div>
                )}
                <div className="ts-modal-field">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={fileEmail}
                    onChange={(e) => setFileEmail(e.target.value)}
                    placeholder="votre@email.com"
                    autoFocus
                  />
                </div>
                <p style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.5rem' }}>
                  Seules les ressources qui vous sont destinées seront affichées.
                </p>
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                  <button
                    type="button"
                    className="ts-btn-secondary"
                    onClick={() => setShowEmailModal(false)}
                    style={{ flex: 1 }}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="ts-btn-primary"
                    disabled={loadingFiles}
                    style={{ flex: 1 }}
                  >
                    {loadingFiles ? '⏳ Vérification...' : '📂 Voir mes ressources'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Exam Registration Modal */}
        {examRegistration && (
          <div className="ts-modal-overlay" onClick={() => setExamRegistration(null)}>
            <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ts-modal-header">
                <h3>📝 Inscription à l'examen</h3>
                <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                  {examRegistration.item_title}
                </p>
              </div>
              <form onSubmit={handleExamRegSubmit} className="ts-modal-body">
                {examRegError && (
                  <div style={{
                    padding: '0.5rem 0.75rem', borderRadius: '0.5rem',
                    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
                    color: '#f87171', fontSize: '0.85rem', marginBottom: '0.75rem'
                  }}>
                    {examRegError}
                  </div>
                )}
                <div className="ts-modal-field">
                  <label>Nom *</label>
                  <input
                    type="text"
                    value={examCandidate.nom}
                    onChange={(e) => setExamCandidate(p => ({ ...p, nom: e.target.value }))}
                    placeholder="Votre nom"
                    autoFocus
                  />
                </div>
                <div className="ts-modal-field">
                  <label>Prénom *</label>
                  <input
                    type="text"
                    value={examCandidate.prenom}
                    onChange={(e) => setExamCandidate(p => ({ ...p, prenom: e.target.value }))}
                    placeholder="Votre prénom"
                  />
                </div>
                <div className="ts-modal-field">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={examCandidate.email}
                    onChange={(e) => setExamCandidate(p => ({ ...p, email: e.target.value }))}
                    placeholder="votre@email.com"
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                  <button
                    type="button"
                    className="ts-btn-secondary"
                    onClick={() => setExamRegistration(null)}
                    style={{ flex: 1 }}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="ts-btn-primary"
                    style={{ flex: 1 }}
                  >
                    Commencer l'examen
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="ts-footer">
          {brandFooter} <a href={brandUrl}>{brandName}</a>{brandExtra ? ` — ${brandExtra}` : ''}
        </div>
      </div>
    </main>
  );
}
